import { getElectronAPI } from "@/modules/electron/api";
import { URL } from "url";
import { RecallPermissionKind, RecallPermissionStatus } from "@/modules/electron/types";

export const electronBindingNavigateToInternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToInternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingNavigateToExternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToExternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingFindInPage = async ({
  text,
  options,
}: {
  text: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: any;
}) => {
  const electronApi = getElectronAPI();

  await electronApi?.findInPage({ text, options });
};

export const electronBindingStopFindInPage = async ({
  action,
}: {
  action: "clearSelection" | "keepSelection";
}) => {
  const electronApi = getElectronAPI();

  await electronApi?.stopFindInPage({ action });
};

export const electronBindingOnFindInPageResult = async ({
  callback,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (event: any, result: any) => void;
}) => {
  const electronApi = getElectronAPI();

  electronApi?.onFindInPageResult(callback);
};

export const electronBindingToggleMenu = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.toggleMenu();
};

export const electronBindingMinimizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.minimizeWindow();
};

export const electronBindingMaximizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.maximizeWindow();
};

export const electronBindingCloseWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.closeWindow();
};

interface ElectronBindingUpdateNowOptions {
  waitForUpdate?: boolean;
}
export const electronBindingUpdateNow = ({
  waitForUpdate = false,
}: ElectronBindingUpdateNowOptions) => {
  const electronApi = getElectronAPI();

  return electronApi?.updateNow(waitForUpdate);
};

export const electronBindingRestartApp = async () => {
  const electronApi = getElectronAPI();

  if (electronApi?.restartApp) {
    await electronApi.restartApp();
  }
};

export const electronBindingOpenSystemSoundSettings = async ({
  focus,
}: {
  focus?: "input" | "output";
} = {}) => {
  const electronApi = getElectronAPI();
  await electronApi?.openSystemSoundSettings?.({ focus });
};

// ==========================
// Recall.ai Desktop SDK Bindings
// ==========================

export const recallBindingPrepareDesktopRecording = async () => {
  const electronApi = getElectronAPI();
  return (await electronApi?.recall.prepareDesktopRecording()) || { windowId: "" };
};

export const recallBindingStartRecording = async (args: {
  windowId: string;
  uploadToken: string;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.startRecording({
    windowId: args.windowId,
    uploadToken: args.uploadToken,
  });
};

export const recallBindingPauseRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.pause({ windowId: args.windowId });
};

export const recallBindingResumeRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.resume({ windowId: args.windowId });
};

export const recallBindingStopRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.stop({ windowId: args.windowId });
};

export const recallBindingOnEvent = async ({
  callback,
}: {
  callback: (payload: { event: unknown }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onEvent?.(callback);
};

export const recallBindingGetPermissionStatus = async (): Promise<
  Record<RecallPermissionKind, RecallPermissionStatus>
> => {
  const electronApi = getElectronAPI();

  const status = await electronApi?.recall.getPermissionStatus();

  if (!status) {
    return {
      [RecallPermissionKind.Accessibility]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.ScreenCapture]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.Microphone]: RecallPermissionStatus.NotRequested,
    };
  }

  return status;
};

export const recallBindingRequestPermission = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.requestPermission(permission);
};

export const recallBindingOpenPermissionPane = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.openPermissionPane(permission);
};

export const recallBindingOnPermissionStatus = async ({
  callback,
}: {
  callback: (payload: {
    event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
  }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onPermissionStatus?.(callback);
};
